/**
 * The Blueprint view (T2.13) — a port of the generator's `sheetHtml`, plus the
 * week highlighter's state.
 *
 * What is ported: the masthead with its mark and overall figure, the corridor
 * and stacked room bands, sliver grouping, the deliverable chips, the warning
 * bar, the week bar and note, and the legend. Both themes come from the ported
 * CSS unchanged; this component sets no colours of its own.
 *
 * Click-to-cycle arrives as an optional `onCycle`. Publish and upload live in the
 * blueprint bar, not here: this component draws a model and knows nothing of
 * versions or permission.
 *
 * Every percentage on this screen comes from the `BlueprintModel` the API
 * computed with the shared `buildModel`. This file performs no arithmetic on a
 * state value at all — grep it for STATE_VALUE and you will find nothing, which
 * is the point.
 */
import { useMemo, useState } from 'react';
import '../../styles/progress.css';
import type { BlueprintModel } from '@blueprint/shared';
import { useTerms } from '../../lib/terms';
import type { ScheduleRow } from '../../lib/blueprints';
import { RoomBand } from './RoomBand';
import { WarningBar } from './WarningBar';
import { WeekBar, WeekNote, type WeekSummary } from './WeekBar';
import { ProgressBar } from './ProgressBar';
import { ThemeMark } from './themeMarks';
import { useWeekSeries } from '../../lib/weekSeries';
import { STATES, stateTermKey } from './stateTerms';
import {
  buildWeeks,
  dayFmt,
  defaultWeekIndex,
  highlightedIndices,
  itemsInWeek,
  resolveScheduleItems,
} from './weeks';

/** The fixed meanings from design.md § 2.1 — these are NOT overridable, the labels are. */
const STATE_MEANING: Record<string, string> = {
  Draft: 'not started',
  Manuscript: 'in progress',
  Proof: 'done, not yet signed off',
  Published: 'approved',
};

function Legend() {
  const terms = useTerms();
  return (
    <div className="legend">
      <span className="lg-title">{terms.noun('deliverable')} state</span>
      {STATES.map((state) => (
        <span className="item" key={state}>
          {/* The swatch reads its colour from the canonical custom property, so a
              renamed state keeps its measured contrast. */}
          <span className="sw" style={{ background: `var(--${state.toLowerCase()})` }} />
          {terms.state(stateTermKey(state))} — {STATE_MEANING[state]}
        </span>
      ))}
      {/* T7.33. The two owner markers, with their words, so the shapes on the
          chips never have to be guessed at — including on paper, where there is
          no tooltip to hover. */}
      <span className="item">
        <span className="owner own-consultant" aria-hidden="true" /> Consultant
        <span className="lg-sep">·</span>
        <span className="owner own-client" aria-hidden="true" /> Client — who owns the{' '}
        {terms.noun('deliverable').toLowerCase()}
      </span>
      <span className="item">
        {terms.noun('room')} % = {terms.noun('pillar').toLowerCase()}-weighted ·{' '}
        {terms.plural('deliverable').toLowerCase()} count equally within a{' '}
        {terms.noun('pillar').toLowerCase()} · {terms.plural('sliver').toLowerCase()} are labels,
        not weights
      </span>
    </div>
  );
}

export function BlueprintView({
  model,
  schedule,
  blueprintId,
  week,
  onCycle,
}: {
  model: BlueprintModel;
  schedule: ScheduleRow[];
  /**
   * T7.26. The weekly progress series is a property of the BLUEPRINT rather
   * than of the version on screen, so it needs the id the route holds.
   * Required, and requested for every role: the series is readable by anyone
   * who can see the blueprint, and a viewer watching progress week by week is
   * the main audience for the slider.
   */
  blueprintId: string;
  /**
   * T9.1-T9.5. What the weekly slider is doing, decided by the route because
   * the URL is the single source of truth for what is rendered.
   *
   * `preview` says what the SHEET below should be. 'none' is the ordinary case,
   * where `model` is the open version. Otherwise `model` is the previewed
   * week's version, except for 'blank' and the two unhappy states, where the
   * sheet is replaced outright — a week before any version shows an empty
   * state, deliberately NOT a sheet of zeros, because nothing reported is not
   * the same as no progress (design.md § 10c).
   */
  week: {
    committedWeek: string | null;
    committedVersionId: string | null;
    onCommit: (weekStart: string) => void;
    preview: 'none' | 'loading' | 'ready' | 'blank' | 'error';
  };
  /** Passed only when the caller may edit this version; see DeliverableChip. */
  onCycle?: (index: number) => void;
}) {
  const terms = useTerms();

  // Viewer+ on the API, and every row in it is published, so it is requested
  // for every role — editor and viewer alike. A failure leaves the slider
  // absent rather than the page broken: the sheet below does not depend on it.
  const series = useWeekSeries(blueprintId);

  const items = useMemo(
    () => resolveScheduleItems(schedule, model.rooms),
    [schedule, model.rooms],
  );
  const weeks = useMemo(() => buildWeeks(items), [items]);

  // -1 means "no week highlighted". The initial value is the week containing
  // today, clamped into the plan's range, exactly as the generator defaults.
  const [selected, setSelected] = useState<number>(() => defaultWeekIndex(weeks));

  const { highlighted, summary } = useMemo<{
    highlighted: ReadonlySet<number>;
    summary: WeekSummary;
  }>(() => {
    const week = selected >= 0 ? weeks[selected] : undefined;
    if (!week) return { highlighted: new Set<number>(), summary: { week: null, count: 0, names: [] } };

    const active = itemsInWeek(items, week);
    const set = highlightedIndices(model.raw.deliverables, active);
    // One room can contribute several schedule rows, one per person, so the
    // names are de-duplicated before they are shown.
    const names = [...new Set(active.map((i) => i.name))];
    return { highlighted: set, summary: { week, count: set.size, names } };
  }, [selected, weeks, items, model.raw.deliverables]);

  const meta = model.meta ?? {};
  const title = typeof meta.Title === 'string' && meta.Title ? meta.Title : 'Programme Blueprint';
  const snapshot = meta.SnapshotDate ? `· ${String(meta.SnapshotDate)}` : '';
  const subtitle = typeof meta.Subtitle === 'string' && meta.Subtitle ? ` · ${meta.Subtitle}` : '';

  /**
   * Whether the SHEET is drawn at all. Everything that is not 'none' or 'ready'
   * replaces it, and the slider stays above whatever replaces it — it is the
   * control that got you here and must be the control that gets you back.
   */
  const showSheet =
    (week.preview === 'none' || week.preview === 'ready') && model.rooms.length > 0;

  return (
    <>
      {showSheet ? <WarningBar warnings={model.warnings} /> : null}

      {showSheet ? (
        <WeekBar
          weeks={weeks}
          selected={selected}
          summary={summary}
          onSelect={setSelected}
          onClear={() => setSelected(-1)}
        />
      ) : null}

      {series.data ? (
        <ProgressBar
          series={series.data}
          committedWeek={week.committedWeek}
          committedVersionId={week.committedVersionId}
          onCommit={week.onCommit}
        />
      ) : null}

      {showSheet ? (
        <div className="sheet">
          <div className="masthead">
            <div>
              <h1>
                {terms.noun('blueprint')} Capability &middot; Programme {terms.noun('blueprint')}
              </h1>
              <p className="programme">{title}</p>
              <p className="meta">
                Programme snapshot {snapshot}
                {subtitle}
              </p>
            </div>
            <ThemeMark name={meta.ThemeMark} />
            <div className="stat">
              <div className="pct">{model.overallPct}%</div>
              <div className="lbl">Complete{model.weightNote ? ' *' : ''}</div>
              {model.weightNote ? <div className="lbl">* {model.weightNote}</div> : null}
            </div>
          </div>

          <WeekNote summary={summary} />

          <div className="floor">
            <div className="corridor" />
            <div className="rooms">
              {model.rooms.map((room) => (
                <RoomBand
                  room={room}
                  highlighted={highlighted}
                  {...(onCycle ? { onCycle } : {})}
                  key={String(room.RoomID)}
                />
              ))}
            </div>
          </div>

          <Legend />
        </div>
      ) : week.preview === 'none' || week.preview === 'ready' ? (
        <>
          <WarningBar warnings={model.warnings} />
          <div className="empty">
            No {terms.plural('room').toLowerCase()} found in this {terms.noun('blueprint')}.
          </div>
        </>
      ) : (
        <PreviewPlaceholder state={week.preview} weekStart={week.committedWeek} />
      )}
    </>
  );
}

/**
 * What stands in for the sheet when a previewed week has none to show (T9.5).
 *
 * The blank case is the one that matters, and it is deliberately WORDS and not
 * a sheet of zeros. A blueprint of 0% rooms would report that no work had been
 * done in that week; what actually happened is that nobody had reported yet.
 * That is the same distinction the slider's own readout makes with "Not
 * reported", and it is the whole reason this component exists rather than the
 * route simply rendering an empty model.
 */
function PreviewPlaceholder({
  state,
  weekStart,
}: {
  state: 'loading' | 'blank' | 'error';
  weekStart: string | null;
}) {
  const terms = useTerms();
  const when = weekStart ? `the week of ${dayFmt(weekStart)} ${weekStart.slice(0, 4)}` : 'that week';

  if (state === 'loading') {
    return (
      <div className="empty" role="status" aria-live="polite">
        Loading {when}…
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div className="empty" role="alert">
        Could not load the {terms.noun('blueprint')} as at {when}.
        <br />
        Move the slider again, or go back to the version that was open.
      </div>
    );
  }
  return (
    <div className="empty" role="status">
      <b>Nothing had been published by {when}.</b>
      <br />
      No version of this {terms.noun('blueprint')} existed then, so there is nothing to show —
      which is not the same as nothing having been done. Move the slider forward to the first
      week that has a version.
    </div>
  );
}
